import React, { useState } from'react';
import { useNavigate } from'react-router-dom';
import axios from'axios';
import Api from'../../services/api';

const RejectionPopup = ({ booking, pickupLocation, dropoffLocation, location, onClose }) => {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(false);

 // Debug: Log when component renders
 console.log('🎯 RejectionPopup component rendered');
 console.log('📦 Props received:', { 
 hasBooking: !!booking, 
 pickupLocation, 
 dropoffLocation, 
 hasLocation: !!location,
 bookingKeys: booking ? Object.keys(booking) : [],
 locationStateKeys: location?.state ? Object.keys(location.state) : []
 });

 const handleFindAnotherDriver = async () => {
 console.log('🔍 RejectionPopup - Finding another driver');
 console.log('📦 Booking data:', booking);
 console.log('📍 Pickup location:', pickupLocation);
 console.log('📍 Dropoff location:', dropoffLocation);
 console.log('📍 Location state:', location?.state);
 
 setLoading(true);
 
 // Try to get booking details from API if booking ID exists and required fields are missing
 let bookingData = booking;
 const bookingId = booking?.id || booking?._id || booking?.bookingId;
 
 if (bookingId && (!booking?.pickupPincode || !booking?.vehicleType)) {
 console.log('📡 Fetching booking details from API...');
 try {
 const token = localStorage.getItem('uttaapp_token');
 const response = await axios.get(Api.Booking.getBooking(bookingId), {
 headers: {'Authorization':`Bearer ${token}`}
 });
 
 if (response.data.success && response.data.booking) {
 console.log('✅ Fetched booking details from API:', response.data.booking);
 bookingData = { ...booking, ...response.data.booking };
 }
 } catch (error) {
 console.warn('⚠️ Could not fetch booking details:', error);
 }
 }
 
 // Navigate to nearby drivers page with booking data
 // Try to get data from multiple sources to ensure we have everything
 const pincodeValue = 
 bookingData?.pickupPincode || 
 location?.state?.pickupPincode || 
 location?.state?.pincode ||
 null;
 
 const vehicleTypeValue = 
 bookingData?.vehicleType || 
 location?.state?.vehicleType ||
 null;
 
 // Try multiple sources for driverId - the rejected driver's ID
 const driverIdValue = 
 bookingData?.driverId || 
 bookingData?.driver?.driverId ||
 location?.state?.driverId || 
 location?.state?.driver?.driverId ||
 null;
 
 console.log('🔍 Extracted values:', {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 driverId: driverIdValue,
 hasBooking: !!bookingData,
 hasLocationState: !!location?.state,
 bookingDriverId: bookingData?.driverId,
 bookingDriver: bookingData?.driver,
 locationStateDriverId: location?.state?.driverId,
 locationStateDriver: location?.state?.driver,
 fullBookingData: bookingData
 });
 
 setLoading(false);
 
 // Validate required fields
 if (!pincodeValue || !vehicleTypeValue) {
 console.error('❌ Missing required data for navigation:', {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 booking: bookingData,
 locationState: location?.state
 });
 alert('Missing required information (pincode or vehicle type). Please try booking again from the book-ride page.');
 return;
 }
 
 const navigationState = {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 paymentMethod: bookingData?.paymentMethod || location?.state?.paymentMethod ||'cash',
 pickupLocation: pickupLocation || bookingData?.pickupLocation || bookingData?.pickupLocationName || location?.state?.pickupLocation,
 pickupLocationName: bookingData?.pickupLocationName || location?.state?.pickupLocationName || pickupLocation || bookingData?.pickupLocation,
 pickupPincode: pincodeValue,
 pickupLandmark: bookingData?.pickupLandmark || location?.state?.pickupLandmark ||'',
 dropoffLocation: dropoffLocation || bookingData?.dropoffLocation || location?.state?.dropoffLocation,
 dropoffLandmark: bookingData?.dropoffLandmark || location?.state?.dropoffLandmark ||'',
 bookingType: bookingData?.bookingType || location?.state?.bookingType ||'book-now',
 rideId: bookingData?.rideId || location?.state?.rideId || null,
 pickupPhoto: bookingData?.pickupPhoto || location?.state?.pickupPhoto || null,
 startDate: bookingData?.scheduledStartDate || location?.state?.startDate || null,
 startTime: bookingData?.scheduledStartTime || location?.state?.startTime || null,
 endDate: bookingData?.scheduledEndDate || location?.state?.endDate || null,
 excludedDriverId: driverIdValue // Exclude the rejected driver
 };
 
 console.log('🚗 Navigating to nearby drivers with state:', navigationState);
 
 // Close popup first
 if (onClose) {
 onClose();
 }
 
 // Navigate to nearby drivers page
 navigate('/nearby-drivers', { state: navigationState });
 };

 return (
 <div 
 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10004] p-4"style={{ display:'flex', position:'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
 onClick={(e) => {
 // Close on backdrop click
 if (e.target === e.currentTarget && onClose) {
 onClose();
 }
 }}
 >
 <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-slideUp overflow-hidden">
 {/* Header */}
 <div className="bg-gradient-to-r from-red-500 via-red-600 to-red-500 p-6">
 <div className="flex items-center justify-center mb-4">
 <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
 <svg className="w-10 h-10 text-red-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={3} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </div>
 </div>
 <h2 className="text-2xl font-bold text-white text-center mb-2">
 Ride Rejected
 </h2>
 <p className="text-red-50 text-center">
 The driver has rejected your ride request
 </p>
 </div>

 {/* Content */}
 <div className="p-6">
 <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
 <p className="text-red-800 text-sm font-medium">
 Don't worry! You can easily find another driver nearby.
 </p>
 </div>

 {/* Action Buttons */}
 <div className="flex flex-col gap-3">
 <button
 onClick={handleFindAnotherDriver}
 disabled={loading}
 className="w-full px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
 {loading ? (
 <>
 <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
 <span>Loading...</span>
 </>
 ) : (
 <>
 <svg className="w-6 h-6"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
 </svg>
 <span>Find Another Driver</span>
 </>
 )}
 </button>
 <button
 onClick={onClose}
 className="w-full px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-lg transition-all">
 Close
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default RejectionPopup;

